// staff-eval.js - Module 6, evaluator side: the digitised Trainer & Coach
// Evaluation Form. Used by the director (full access) and by head-trainer
// evaluators (RLS limits them to evaluations they authored).
//
// Save model: everything autosaves - header/Part E fields on change/blur,
// ratings on tap (upsert per criterion; tapping the selected word again
// clears it back to unrated). Lifecycle: draft -> lock (freezes the person's
// Part D) -> publish (visible in their Coach Hub) -> acknowledge.

import { AUTH } from './auth.js';
import { CONFIG } from './config.js';
import { DB } from './db.js';
import { sectionsForRole, criteriaForRole, ratingWord,
         STAFF_RATING_LEVELS, STAFF_RATING_MEANINGS } from './staff-eval-data.js';
import { t } from './strings.js';
import { UI } from './ui.js';
import { UTILS } from './utils.js';

export const STAFF_EVAL = (() => {

  let _person  = null;   // staff row being evaluated
  let _evals   = [];     // this person's evaluations (RLS-scoped)
  let _eval    = null;   // open evaluation row
  let _ratings = {};     // criterion_key -> { rating, comment }
  let _actions = [];
  let _prevActions = [];
  let _notesSince  = [];
  let _onBack  = null;   // where the list screen returns to
  let _openSection = null; // index of the focused rating section (null = overview)

  // ── EVALUATIONS LIST (per person) ───────────────────────────────────────────

  async function showList(person, onBack) {
    _person = person;
    if (onBack) _onBack = onBack;
    UI.showScreen('screen-staff-eval-list');
    document.getElementById('staff-eval-list-title').textContent =
      t('seval.listTitle', { name: person.full_name });
    UI.setLoading('staff-eval-list-content', t('common.loading'));
    try {
      const all = await DB.staffHub.listEvaluations();
      _evals = (all || []).filter(e => e.staff_id === person.id);
      _renderList();
    } catch (err) {
      console.error('Staff eval list failed:', err);
      UI.setEmpty('staff-eval-list-content', t('common.error'));
    }
  }

  function backFromList() {
    if (_onBack) _onBack();
  }

  function _statusLabel(e) {
    if (e.published_to_staff_at) {
      return e.acknowledged_at
        ? t('seval.statusAcked', { date: UTILS.formatDate(e.acknowledged_at, 'medium') })
        : t('seval.statusPublished');
    }
    return e.locked_at ? t('seval.statusLocked') : t('seval.statusDraft');
  }

  function _renderList() {
    const el = document.getElementById('staff-eval-list-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;

    const rows = _evals.length
      ? _evals.map(e => `
          <button class="player-eval-card" data-eval-open="${esc(e.id)}">
            <div class="player-eval-card__main">
              <div class="player-eval-card__title">${esc(UTILS.formatDate(e.evaluated_on, 'medium'))} · ${esc(e.role_assessed)}</div>
              <div class="player-eval-card__sub">${esc(e.evaluator_name || '')}${e.season_id ? ` · ${esc(e.season_id)}` : ''}</div>
            </div>
            <span class="player-read-chip">${esc(_statusLabel(e))}</span>
          </button>`).join('')
      : `<div class="sel-detail-hint">${esc(t('seval.none'))}</div>`;

    el.innerHTML = `
      <div class="sel-detail-actions" style="margin-bottom:12px;">
        <button class="btn btn--primary" id="staff-eval-new">${esc(t('seval.new'))}</button>
      </div>
      ${rows}`;

    document.getElementById('staff-eval-new')?.addEventListener('click', _createNew);
    el.querySelectorAll('[data-eval-open]').forEach(b =>
      b.addEventListener('click', () => showEditor(b.dataset.evalOpen)));
  }

  async function _createNew() {
    // Continuity: link the most recent locked/published evaluation as the
    // previous review; "no previous review" when this is the first.
    const prev = _evals.find(e => e.locked_at || e.published_to_staff_at) || null;
    try {
      const rows = await DB.staffHub.createEvaluation({
        staff_id:        _person.id,
        author_staff_id: AUTH.isDirector() ? null : AUTH.getStaffId(),
        evaluator_name:  AUTH.isDirector() ? 'Director' : AUTH.getStaffName(),
        season_id:       CONFIG.season.current,
        previous_evaluation_id: prev?.id || null,
        no_previous_review:     !prev,
      });
      const created = rows?.[0];
      if (!created?.id) throw new Error('create returned no id');
      _evals.unshift(created);
      showEditor(created.id);
    } catch (err) {
      console.error('Create staff evaluation failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  // ── EDITOR ──────────────────────────────────────────────────────────────────

  async function showEditor(evalId) {
    UI.showScreen('screen-staff-eval-edit');
    document.getElementById('staff-eval-edit-title').textContent =
      t('seval.editTitle', { name: _person?.full_name || '' });
    _openSection = null;   // every evaluation opens on the overview
    UI.setLoading('staff-eval-edit-content', t('common.loading'));

    try {
      _eval = _evals.find(e => e.id === evalId)
           || (await DB.staffHub.listEvaluations()).find(e => e.id === evalId);
      if (!_eval) { UI.setEmpty('staff-eval-edit-content', t('common.error')); return; }

      const [ratings, actions] = await Promise.all([
        DB.staffHub.getRatings(evalId),
        DB.staffHub.getActions(evalId),
      ]);
      _ratings = {};
      (ratings || []).forEach(r => { _ratings[r.criterion_key] = { rating: r.rating, comment: r.comment }; });
      _actions = actions || [];

      // "Since last review" context: the previous review's action points +
      // every feedback note (sent AND private) since that review's date.
      _prevActions = [];
      _notesSince  = [];
      if (_eval.previous_evaluation_id) {
        _prevActions = await DB.staffHub.getActions(_eval.previous_evaluation_id).catch(() => []);
      }
      const prev  = _evals.find(e => e.id === _eval.previous_evaluation_id);
      const since = prev ? `${prev.evaluated_on}T00:00:00Z` : null;
      _notesSince = await DB.staffHub.notesSince(_eval.staff_id, since).catch(() => []);

      _renderEditor();
    } catch (err) {
      console.error('Staff eval editor load failed:', err);
      UI.setEmpty('staff-eval-edit-content', t('common.error'));
    }
  }

  async function _patch(data) {
    try {
      await DB.staffHub.updateEvaluation(_eval.id, data);
      Object.assign(_eval, data);
    } catch (err) {
      console.error('Staff eval save failed:', err);
      UI.toast(t('common.error'), 'error');
    }
  }

  function _renderEditor() {
    const el = document.getElementById('staff-eval-edit-content');
    if (!el) return;
    const esc = UTILS.escapeHtml;
    const ev  = _eval;
    const name = _person?.full_name || '';
    const locked    = !!ev.locked_at;
    const published = !!ev.published_to_staff_at;

    const sections = sectionsForRole(ev.role_assessed);
    const allKeys  = criteriaForRole(ev.role_assessed);
    const ratedCount = allKeys.filter(k => _ratings[k] !== undefined).length;

    // Focused single-section mode (director request 2026-07-02: the full form
    // is too long as one scroll - pick the part you are evaluating, then get
    // large tap targets). State survives re-renders, so each rating tap keeps
    // the evaluator inside their section.
    if (_openSection != null && sections[_openSection]) {
      const s = sections[_openSection];
      const sectionDone = s.criteria.filter(c => _ratings[c.key] !== undefined).length;
      el.innerHTML = `
        <div class="sel-detail-actions" style="margin-bottom:10px;">
          <button class="btn btn--secondary" id="seval-back-sections">← ${esc(t('seval.backToSections'))}</button>
        </div>
        <div class="sel-detail-section">
          <div class="sel-detail-section__title">${esc(s.title)}</div>
          <div class="sel-detail-hint" style="margin-bottom:10px;">${esc(t('seval.progress', { done: sectionDone, total: s.criteria.length }))}</div>
          ${s.criteria.map(c => {
            const r = _ratings[c.key];
            const hasComment = !!r?.comment;
            return `
              <div class="seval-criterion seval-criterion--big" data-criterion="${esc(c.key)}">
                <div class="seval-criterion__label">${esc(c.label)}
                  ${hasComment ? `<div class="eval-row__comment">${esc(r.comment)}</div>` : ''}
                </div>
                <div class="seval-rating-row seval-rating-row--big">
                  ${STAFF_RATING_LEVELS.map(l => `
                    <button class="seval-rate-btn seval-rate-btn--big ${r && r.rating === l.value ? 'seval-rate-btn--on' : ''}"
                            data-rate-key="${esc(c.key)}" data-rate-value="${l.value}"
                            title="${esc(l.word)} - ${esc(STAFF_RATING_MEANINGS[l.value])}">
                      <span class="seval-rate-btn__abbr">${esc(l.abbr)}</span>
                      <span class="seval-rate-btn__word">${esc(l.word)}</span>
                    </button>`).join('')}
                  <button class="seval-rate-btn seval-rate-btn--big ${r && r.rating == null ? 'seval-rate-btn--on' : ''}"
                          data-rate-key="${esc(c.key)}" data-rate-value="na">
                    <span class="seval-rate-btn__abbr">${esc(t('seval.ratedNA'))}</span>
                    <span class="seval-rate-btn__word">${esc(t('seval.naWord'))}</span>
                  </button>
                  <button class="seval-rate-btn seval-rate-btn--big seval-rate-btn--comment ${hasComment ? 'seval-rate-btn--on' : ''}"
                          data-comment-key="${esc(c.key)}">✎</button>
                </div>
              </div>`;
          }).join('')}
        </div>
        <div class="sel-detail-actions">
          <button class="btn btn--secondary" id="seval-back-sections-bottom">← ${esc(t('seval.backToSections'))}</button>
        </div>`;
      _bindEditor();
      return;
    }

    const roleBtn = (value, label) => `
      <button class="sel-section-toggle ${ev.role_assessed === value ? 'sel-section-toggle--on' : ''}"
              data-role-pick="${value}">${esc(label)}</button>`;

    const choiceBtn = (field, value, label) => `
      <button class="sel-section-toggle ${ev[field] === value ? 'sel-section-toggle--on' : ''}"
              data-choice-field="${field}" data-choice-value="${value}">${esc(label)}</button>`;

    // Overview mode: one card per section with its progress - tap to open the
    // focused view. The 43 inline criteria are gone from the scroll.
    const sectionsHtml = `
      <div class="sel-detail-section">
        <div class="sel-detail-section__title">${esc(t('seval.sectionsTitle'))}</div>
        <div class="sel-detail-hint" style="margin-bottom:8px;">${esc(t('seval.sectionsHint'))}</div>
        <div class="seval-section-grid">
          ${sections.map((s, i) => {
            const done = s.criteria.filter(c => _ratings[c.key] !== undefined).length;
            const full = done === s.criteria.length;
            return `
              <button class="seval-section-card ${full ? 'seval-section-card--done' : ''}" data-open-section="${i}">
                <span class="seval-section-card__title">${esc(s.title)}</span>
                <span class="seval-section-card__count">${full ? '✓ ' : ''}${done}/${s.criteria.length}</span>
              </button>`;
          }).join('')}
        </div>
      </div>`;

    const notesHtml = _notesSince.length
      ? _notesSince.map(n => `
          <div class="snote-row ${n.visibility === 'private' ? 'snote-row--private' : ''}">
            <div class="snote-row__meta">${esc(UTILS.formatDate(n.created_at, 'medium'))} · ${esc(n.category)}
              ${n.visibility === 'private' ? `<span class="snote-private-tag">${esc(t('snote.privateTag'))}</span>` : ''}</div>
            <div>${esc(n.body)}</div>
          </div>`).join('')
      : `<div class="sel-detail-hint">${esc(t('seval.sinceLastNone'))}</div>`;

    el.innerHTML = `
      <div class="sel-detail-section">
        <div class="sel-detail-section__title">${esc(t('seval.header'))}</div>
        <div class="gate__label">${esc(t('seval.role'))}</div>
        <div class="sel-section-toggles">
          ${roleBtn('trainer', t('seval.roleTrainer'))}
          ${roleBtn('coach',   t('seval.roleCoach'))}
          ${roleBtn('both',    t('seval.roleBoth'))}
        </div>
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.team'))}</div>
        <input class="field" id="seval-team" value="${esc(ev.team_label || '')}">
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.ageCategory'))}</div>
        <input class="field" id="seval-agecat" value="${esc(ev.age_category || '')}">
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.date'))}</div>
        <input class="field" id="seval-date" type="date" value="${esc(ev.evaluated_on || '')}">
      </div>

      <div class="sel-detail-section">
        <div class="sel-detail-section__title">${esc(t('seval.compliance'))}</div>
        <div class="gate__label">${esc(t('seval.diploma'))}</div>
        <input class="field" id="seval-diploma" value="${esc(ev.diploma_held || '')}">
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.diplomaReq'))}</div>
        <div class="sel-section-toggles">
          ${choiceBtn('meets_diploma_req', 'yes',          t('seval.reqYes'))}
          ${choiceBtn('meets_diploma_req', 'no',           t('seval.reqNo'))}
          ${choiceBtn('meets_diploma_req', 'not_required', t('seval.reqNotRequired'))}
          ${choiceBtn('meets_diploma_req', 'in_progress',  t('seval.reqInProgress'))}
        </div>
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.safeguarding'))}</div>
        <div class="sel-section-toggles">
          ${choiceBtn('safeguarding_signed', 'on_file',   t('seval.sgOnFile'))}
          ${choiceBtn('safeguarding_signed', 'follow_up', t('seval.sgFollowUp'))}
        </div>
      </div>

      <div class="sel-detail-hint" style="margin:4px 0 8px;">${esc(t('seval.progress', { done: ratedCount, total: allKeys.length }))}</div>
      ${sectionsHtml}

      <div class="sel-detail-section">
        <div class="sel-detail-section__title">${esc(t('seval.partD', { name }))}</div>
        ${(ev.reflection_well || ev.reflection_improve || ev.reflection_support) ? `
          <div class="eval-text-label">${esc(t('seval.partDWell'))}</div>
          <div class="eval-text-block">${esc(ev.reflection_well || '-')}</div>
          <div class="eval-text-label">${esc(t('seval.partDImprove'))}</div>
          <div class="eval-text-block">${esc(ev.reflection_improve || '-')}</div>
          <div class="eval-text-label">${esc(t('seval.partDSupport'))}</div>
          <div class="eval-text-block">${esc(ev.reflection_support || '-')}</div>`
        : `<div class="sel-detail-hint">${esc(t('seval.partDEmpty', { name }))}</div>`}
      </div>

      <div class="sel-detail-section">
        <div class="sel-detail-section__title">${esc(t('seval.partE'))}</div>
        <div class="gate__label">${esc(t('seval.progressPrev'))}</div>
        <div class="sel-section-toggles" style="margin-bottom:6px;">
          <button class="sel-section-toggle ${ev.no_previous_review ? 'sel-section-toggle--on' : ''}" id="seval-noprev">${esc(t('seval.noPrev'))}</button>
        </div>
        ${_prevActions.length ? `
          <div class="eval-text-label">${esc(t('seval.prevActions'))}</div>
          ${_prevActions.map(a => `<div class="eval-text-block">${a.status === 'done' ? '✓ ' : '○ '}${esc(a.body)}</div>`).join('')}` : ''}
        <textarea class="prompt-input" id="seval-progressprev" rows="3">${esc(ev.progress_previous || '')}</textarea>
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.strengths'))}</div>
        <textarea class="prompt-input" id="seval-strengths" rows="3">${esc(ev.strengths || '')}</textarea>
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.development'))}</div>
        <textarea class="prompt-input" id="seval-development" rows="3">${esc(ev.development_points || '')}</textarea>
        <div class="gate__label" style="margin-top:10px;">${esc(t('seval.actions'))}</div>
        ${_actions.map(a => `
          <div class="access-eval-row">
            <span style="flex:1;">${a.status === 'done' ? '✓ ' : ''}${esc(a.body)}</span>
            <button class="sel-roster-btn" data-action-toggle="${a.id}" data-action-next="${a.status === 'done' ? 'open' : 'done'}">
              ${esc(a.status === 'done' ? t('seval.actionReopen') : t('seval.actionDone'))}</button>
            <button class="sel-roster-btn sel-roster-btn--danger" data-action-del="${a.id}">🗑</button>
          </div>`).join('')}
        <button class="btn btn--secondary" id="seval-add-action" style="margin-top:8px;">${esc(t('seval.addAction'))}</button>
      </div>

      <div class="sel-detail-section">
        <div class="sel-detail-section__title">${esc(t('seval.sinceLast'))}</div>
        ${notesHtml}
      </div>

      <div class="sel-detail-section">
        <div class="sel-detail-hint">${published ? esc(t('seval.publishedHint', { name }))
                                     : locked   ? esc(t('seval.lockedHint')) : ''}</div>
        <div class="sel-detail-actions">
          ${locked
            ? `<button class="btn btn--secondary" id="seval-unlock">${esc(t('seval.unlock'))}</button>`
            : `<button class="btn btn--secondary" id="seval-lock">${esc(t('seval.lock'))}</button>`}
          ${published
            ? `<button class="btn btn--ghost" id="seval-unpublish">${esc(t('seval.unpublish'))}</button>`
            : `<button class="btn btn--primary" id="seval-publish">${esc(t('seval.publish', { name }))}</button>`}
          <button class="btn btn--ghost" id="seval-delete" style="color:var(--color-red-light);">${esc(t('seval.delete'))}</button>
        </div>
      </div>
    `;

    _bindEditor();
  }

  function _bindEditor() {
    const el = document.getElementById('staff-eval-edit-content');
    if (!el) return;

    el.querySelectorAll('[data-open-section]').forEach(b =>
      b.addEventListener('click', () => {
        _openSection = Number(b.dataset.openSection);
        _renderEditor();
        document.getElementById('staff-eval-edit-content')?.scrollIntoView();
      }));
    ['seval-back-sections', 'seval-back-sections-bottom'].forEach(id =>
      document.getElementById(id)?.addEventListener('click', () => {
        _openSection = null;
        _renderEditor();
      }));

    el.querySelectorAll('[data-role-pick]').forEach(b =>
      b.addEventListener('click', async () => {
        await _patch({ role_assessed: b.dataset.rolePick });
        _openSection = null;   // the section list changes with the role
        _renderEditor();
      }));

    el.querySelectorAll('[data-choice-field]').forEach(b =>
      b.addEventListener('click', async () => {
        const field = b.dataset.choiceField;
        const value = _eval[field] === b.dataset.choiceValue ? null : b.dataset.choiceValue;
        await _patch({ [field]: value });
        _renderEditor();
      }));

    const blurSave = (id, field) => {
      const input = document.getElementById(id);
      input?.addEventListener('blur', () => {
        const v = input.value.trim();
        if ((_eval[field] || '') !== v) _patch({ [field]: v || null });
      });
    };
    blurSave('seval-team',         'team_label');
    blurSave('seval-agecat',       'age_category');
    blurSave('seval-diploma',      'diploma_held');
    blurSave('seval-progressprev', 'progress_previous');
    blurSave('seval-strengths',    'strengths');
    blurSave('seval-development',  'development_points');
    document.getElementById('seval-date')?.addEventListener('change', (e) => {
      if (e.target.value) _patch({ evaluated_on: e.target.value });
    });

    document.getElementById('seval-noprev')?.addEventListener('click', async () => {
      await _patch({ no_previous_review: !_eval.no_previous_review });
      _renderEditor();
    });

    // Ratings: tap a word to set it; tap the same word again to clear.
    el.querySelectorAll('[data-rate-key]').forEach(b =>
      b.addEventListener('click', async () => {
        const key = b.dataset.rateKey;
        const value = b.dataset.rateValue === 'na' ? null : parseInt(b.dataset.rateValue, 10);
        const current = _ratings[key];
        try {
          if (current !== undefined && current.rating === value) {
            await DB.staffHub.deleteRating(_eval.id, key);
            delete _ratings[key];
          } else {
            await DB.staffHub.saveRating(_eval.id, key, value, current?.comment || null);
            _ratings[key] = { rating: value, comment: current?.comment || null };
          }
          _renderEditor();
        } catch (err) {
          console.error('Rating save failed:', err);
          UI.toast(t('common.error'), 'error');
        }
      }));

    el.querySelectorAll('[data-comment-key]').forEach(b =>
      b.addEventListener('click', async () => {
        const key = b.dataset.commentKey;
        const current = _ratings[key];
        const text = await UI.promptTextAsync({
          title: t('seval.addComment'),
          value: current?.comment || '',
        });
        if (text === undefined || text === null) return;
        try {
          await DB.staffHub.saveRating(_eval.id, key,
            current !== undefined ? current.rating : null, text.trim() || null);
          _ratings[key] = { rating: current !== undefined ? current.rating : null,
                            comment: text.trim() || null };
          _renderEditor();
        } catch (err) {
          console.error('Comment save failed:', err);
          UI.toast(t('common.error'), 'error');
        }
      }));

    document.getElementById('seval-add-action')?.addEventListener('click', async () => {
      const text = (await UI.promptTextAsync({ title: t('seval.addAction') }) || '').trim();
      if (!text) return;
      try {
        await DB.staffHub.addAction(_eval.id, text, _actions.length);
        _actions = await DB.staffHub.getActions(_eval.id);
        _renderEditor();
      } catch (err) {
        console.error('Add action failed:', err);
        UI.toast(t('common.error'), 'error');
      }
    });
    el.querySelectorAll('[data-action-toggle]').forEach(b =>
      b.addEventListener('click', async () => {
        await DB.staffHub.setActionStatus(b.dataset.actionToggle, b.dataset.actionNext).catch(() => {});
        _actions = await DB.staffHub.getActions(_eval.id);
        _renderEditor();
      }));
    el.querySelectorAll('[data-action-del]').forEach(b =>
      b.addEventListener('click', async () => {
        await DB.staffHub.deleteAction(b.dataset.actionDel).catch(() => {});
        _actions = await DB.staffHub.getActions(_eval.id);
        _renderEditor();
      }));

    document.getElementById('seval-lock')?.addEventListener('click', async () => {
      await _patch({ locked_at: new Date().toISOString() });
      _renderEditor();
    });
    document.getElementById('seval-unlock')?.addEventListener('click', async () => {
      await _patch({ locked_at: null });
      _renderEditor();
    });
    document.getElementById('seval-publish')?.addEventListener('click', async () => {
      if (!_eval.locked_at) { UI.toast(t('seval.publishNeedsLock'), 'info'); return; }
      await _patch({ published_to_staff_at: new Date().toISOString() });
      _renderEditor();
    });
    document.getElementById('seval-unpublish')?.addEventListener('click', async () => {
      await _patch({ published_to_staff_at: null });
      _renderEditor();
    });
    document.getElementById('seval-delete')?.addEventListener('click', () => {
      UI.modal({
        title: t('seval.deleteTitle'),
        body:  t('seval.deleteBody'),
        buttons: [
          { label: t('seval.delete'), style: 'primary', action: async () => {
            try {
              await DB.staffHub.deleteEvaluation(_eval.id);
              showList(_person);
            } catch (err) {
              console.error('Delete evaluation failed:', err);
              UI.toast(t('common.error'), 'error');
            }
          } },
          { label: 'Cancel' },
        ],
      });
    });
  }

  function backFromEditor() {
    showList(_person);
  }

  // ── SHARED READ-ONLY RENDERER (used by the staff-side Coach Hub) ────────────

  function renderReadOnly(container, ev, ratings, actions) {
    const esc = UTILS.escapeHtml;
    const byKey = {};
    (ratings || []).forEach(r => { byKey[r.criterion_key] = r; });

    let html = `<div class="eval-meta">${esc(ev.evaluator_name || '')} · ${esc(UTILS.formatDate(ev.evaluated_on, 'medium'))}${ev.team_label ? ` · ${esc(ev.team_label)}` : ''}</div>`;

    sectionsForRole(ev.role_assessed).forEach(s => {
      const rows = s.criteria.filter(c => byKey[c.key] !== undefined);
      if (!rows.length) return;
      html += `<div class="eval-section"><div class="eval-section__title">${esc(s.title)}</div>`;
      rows.forEach(c => {
        const r = byKey[c.key];
        html += `
          <div class="eval-row">
            <div>
              <div class="eval-row__skill">${esc(c.label)}</div>
              ${r.comment ? `<div class="eval-row__comment">${esc(r.comment)}</div>` : ''}
            </div>
            <span class="seval-word seval-word--${r.rating ?? 'na'}">${esc(ratingWord(r.rating))}</span>
          </div>`;
      });
      html += `</div>`;
    });

    if (ev.reflection_well || ev.reflection_improve || ev.reflection_support) {
      html += `<div class="eval-section"><div class="eval-section__title">${esc(t('chub.reflectionTitle'))}</div>
        <div class="eval-text-label">${esc(t('seval.partDWell'))}</div><div class="eval-text-block">${esc(ev.reflection_well || '-')}</div>
        <div class="eval-text-label">${esc(t('seval.partDImprove'))}</div><div class="eval-text-block">${esc(ev.reflection_improve || '-')}</div>
        <div class="eval-text-label">${esc(t('seval.partDSupport'))}</div><div class="eval-text-block">${esc(ev.reflection_support || '-')}</div>
      </div>`;
    }

    html += `<div class="eval-section"><div class="eval-section__title">${esc(t('seval.partE'))}</div>`;
    if (ev.no_previous_review) {
      html += `<div class="eval-text-label">${esc(t('seval.progressPrev'))}</div><div class="eval-text-block">${esc(t('seval.noPrev'))}</div>`;
    } else if (ev.progress_previous) {
      html += `<div class="eval-text-label">${esc(t('seval.progressPrev'))}</div><div class="eval-text-block">${esc(ev.progress_previous)}</div>`;
    }
    if (ev.strengths)          html += `<div class="eval-text-label">${esc(t('seval.strengths'))}</div><div class="eval-text-block">${esc(ev.strengths)}</div>`;
    if (ev.development_points) html += `<div class="eval-text-label">${esc(t('seval.development'))}</div><div class="eval-text-block">${esc(ev.development_points)}</div>`;
    if ((actions || []).length) {
      html += `<div class="eval-text-label">${esc(t('seval.actions'))}</div>` +
        actions.map(a => `<div class="eval-text-block">${a.status === 'done' ? '✓ ' : '○ '}${esc(a.body)}</div>`).join('');
    }
    html += `</div>`;

    container.innerHTML = html;
  }

  return { showList, showEditor, backFromList, backFromEditor, renderReadOnly };

})();
